import React, { useState } from "react"
import Dialog from "../../dialog/dialog"
import { StaticImage } from "gatsby-plugin-image"
import * as styles from "./index.module.css"
import BodyText from "../../text/body-text/body-text"
import Button from "../../button/button"
import { TextField, CustomSelect } from "../utils"
import { useToast } from '../../toast/ToastContext'
import axios from 'axios'
import Joi from 'joi'
import { Formik, Form } from "formik"

const commonImageProps = {
  layout: "constrained",
  placeholder: "blurred",
  formats: ["auto", "webp"],
  outputPixelDensities: [0.5, 0.75, 1, 2],
}

const PartnerForm = ({ isOpen, setIsOpen, clickedFrom }) => {
  const { showToast } = useToast() || {}
  const [isSubmitting, setIsSubmitting] = useState(false)

  const hearAboutUs = [
    { label: "Social-Media", value: "social-media" },
    { label: "Referral", value: "referral" },
    { label: "Google", value: "Google" },
  ]

  const initialValues = {
    companyName: "",
    name: "",
    designation: "",
    email: "",
    phoneNumber: "",
    hear: "",
    website: "",
    partnership: "",
    message: "",
    clickedFrom
  }

  const validationSchema = Joi.object({
    companyName: Joi.string()
      .required().messages({ 'string.empty': 'Company name is required' })
      .pattern(/^(?!.*<script).*$/).message('Script tags are not allowed')
      .pattern(/.*[a-zA-Z].*/).message('Only special characters are not allowed'),

    name: Joi.string()
      .required().messages({ 'string.empty': 'Full name is required' })
      .pattern(/^(?!.*<script).*$/).message('Script tags are not allowed')
      .pattern(/.*[a-zA-Z].*/).message('Only special characters are not allowed'),

    email: Joi.string()
      .required().messages({ 'string.empty': 'Email is required' })
      .pattern(/^(?!.*<script).*$/).message('Script tags are not allowed')
      .pattern(/^[\w.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/).message('Invalid email format'),

    phoneNumber: Joi.string()
      .required().messages({ 'string.empty': 'Phone number is required' })
      .pattern(/^[0-9+\-]*$/).message('Only numbers, + or - are allowed')
      .min(8).message('Minimum length is 8 digits')
      .max(20).message('Maximum length is 20 digits'),
  }).unknown(true)

  const validateWithJoi = values => {
    const { error } = validationSchema.validate(values, { abortEarly: false })
    const errors = {}
    if (error) {
      error.details.forEach(detail => {
        const field = detail.path[0]
        errors[field] = detail.message
      })
    }
    return errors
  }

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setIsSubmitting(true)
      await axios.post(`${process.env.GATSBY_API_URL}/learnet.common.db.apis.CommonDbAPIServiceV1/addPartnerFormData`, values)
      showToast ? showToast("Your request has been sent.", 'success') : alert("Submitted successfully")
      setIsOpen(false)
      resetForm()
    } catch (error) {
      const message = error?.response?.data?.message || "There was an error submitting the form!"
      showToast ? showToast(message, 'error') : alert(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog onClose={() => setIsOpen(false)} isOpen={isOpen} setIsDialogOpen={setIsOpen}>
      <div className="flex flex-col md:flex-row items-stretch">
        <div className="bg-[#3057EE] py-5 px-5 w-full hidden md:w-1/3 md:flex md:flex-col md:items-center md:justify-center md:gap-6">
          <BodyText variant="bodyText5" className="text-white text-center">
            Let's <span className="text-[#FFBD59]">grow</span> strong by collaborating!
          </BodyText>
          <StaticImage
            src="../../../images/geet/enquiryForm/Group 78598.png"
            {...commonImageProps}
          />
        </div>

        <div className="py-5 w-full md:w-2/3 px-8 flex flex-col items-center justify-center">
          <Formik
            initialValues={initialValues}
            validate={validateWithJoi}
            onSubmit={handleSubmit}
          >
            {({ values, handleChange, handleBlur, setFieldValue, errors, touched }) => (
              <Form className="flex flex-col gap-4 w-full">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextField
                    label="Company Name"
                    name="companyName"
                    value={values.companyName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter company name"
                    required
                    error={touched.companyName && !!errors.companyName}
                    errorMessage={errors.companyName}
                  />
                  <TextField
                    label="Contact Person Name"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter name"
                    required
                    error={touched.name && !!errors.name}
                    errorMessage={errors.name}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextField
                    label="Position/Designation"
                    name="designation"
                    value={values.designation}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter Designation"
                  />
                  <TextField
                    fieldType="email"
                    label="Email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter email"
                    required
                    error={touched.email && !!errors.email}
                    errorMessage={errors.email}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextField
                    type="phone"
                    label="Phone Number"
                    name="phoneNumber"
                    value={values.phoneNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter phone number"
                    required
                    error={touched.phoneNumber && !!errors.phoneNumber}
                    errorMessage={errors.phoneNumber}
                  />
                  <CustomSelect
                    label="How did you hear about us?"
                    value={values.hear}
                    onChange={option => setFieldValue("hear", option.value)}
                    options={hearAboutUs}
                  />
                </div>

                <TextField
                  label="Website Url"
                  name="website"
                  value={values.website}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter Website"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextField
                    label="Partnership Interest"
                    name="partnership"
                    value={values.partnership}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Type your description"
                  />
                  <TextField
                    label="Additional Notes/Message"
                    name="message"
                    value={values.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Type your description"
                  />
                </div>

                <div className="text-center mt-6 mx-auto">
                  <Button className="mb-4" type="submit" buttonProps={{ disabled: isSubmitting }}>
                    Submit
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </Dialog>
  )
}

export default PartnerForm
